import React from 'react';
import { Button } from '@oysterjs/ui/button';

import {
  removeBlankFieldSentence,
  selectBlankField
} from '~/components/email-templates/blank-field-actions';
import { BlankFieldNotice } from './artifact-focus-email-blank-field-notice';
import { TemplateHighlightSwitch } from './artifact-focus-email-highlight';
import { InlineNotice } from '~/components/inline-notice';
import { OlderVersionNotice } from './artifact-focus-email-older-version-notice';
import { ReplaceEditsDialog } from './artifact-focus-email-template-picker';
import type { ComposerTemplateState } from './hooks/use-composer-template-state';

/** What the composer shows above the body of an email drafted from a template. */
export const EmailTemplateNotices = ({
  template,
  redraftDisabledReason,
  isBulkEmail = false
}: {
  template: ComposerTemplateState;
  /** Why the email can't be redrafted right now, or undefined when it can. */
  redraftDisabledReason: string | undefined;
  /** A bulk draft's email, which only Cara redrafts, when asked in the workspace. */
  isBulkEmail?: boolean;
}) => {
  const [isConfirmingLatestRedraft, setIsConfirmingLatestRedraft] = React.useState(false);
  const { newerTemplate, redraft } = template;
  return (
    <>
      {template.isDraftedFromTemplate && (
        <TemplateHighlightSwitch
          checked={template.isHighlighting}
          onChange={template.setIsHighlighting}
        />
      )}
      {redraft.writingNotice && (
        <InlineNotice className="mb-3" tone="info" title={redraft.writingNotice} />
      )}
      {template.isOlderVersionNoticeShown && newerTemplate && !isBulkEmail && (
        <OlderVersionNotice
          template={newerTemplate}
          redraftDisabledReason={redraftDisabledReason}
          onRedraft={() =>
            template.hasEditedDraft()
              ? setIsConfirmingLatestRedraft(true)
              : template.redraftWithLatestVersion()
          }
          onKeep={template.keepOlderVersion}
        />
      )}
      {newerTemplate && (
        <ReplaceEditsDialog
          open={isConfirmingLatestRedraft}
          pick={{ id: newerTemplate.id, name: newerTemplate.name, whenToUse: null }}
          onCancel={() => setIsConfirmingLatestRedraft(false)}
          onConfirm={() => {
            setIsConfirmingLatestRedraft(false);
            template.redraftWithLatestVersion();
          }}
        />
      )}
      {template.blankFieldLabels.length > 0 && (
        <BlankFieldNotice
          blankFieldLabels={template.blankFieldLabels}
          firstBodyBlankLabel={template.firstBodyBlankLabel}
          onFillIn={(label) => template.actOnBlankField(label, selectBlankField)}
          onRemoveSentence={(label) => template.actOnBlankField(label, removeBlankFieldSentence)}
          offersRedraft={!isBulkEmail}
        />
      )}
      {redraft.hasFailed && (
        <InlineNotice
          className="mb-3"
          tone="error"
          title="Cara couldn't finish this draft."
          description="Try again, or pick a different template. Your earlier draft is still here."
          actions={
            <Button color="white" onClick={() => void redraft.retry()}>
              Try again
            </Button>
          }
        />
      )}
    </>
  );
};
